/** Estados operativos de entregas (Despachos → Entregas).
 * Nombres/colores editables desde Ajustes → Operaciones → Estados Despacho.
 */

import { getActiveDispatchStatusOptions } from '../components/placeholders/commercialCatalogDefaults';

export const DELIVERY_STATUS_OPTIONS = [
  'Solicitada',
  'Programada',
  'Reprogramada',
  'En Reprogramación',
  'En Ruta a Tiempo',
  'En Ruta con Retraso',
  'Entrega Con Incidente',
  'Entrega Conforme',
  'Entrega con Retraso',
  'Cancelado',
];

const fold = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const hexToRgb = (hex) => {
  const raw = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(raw)) return null;
  return {
    r: parseInt(raw.slice(1, 3), 16),
    g: parseInt(raw.slice(3, 5), 16),
    b: parseInt(raw.slice(5, 7), 16),
  };
};

const rgbaFromHex = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const badgeStylesFromHex = (hex, fallback) => {
  const color = String(hex || '').trim();
  const bg = rgbaFromHex(color, 0.14);
  const border = rgbaFromHex(color, 0.4);
  const base = fallback || { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' };
  if (!bg || !border) return base;
  return { bg, text: color, border };
};

/** Filas/opciones activas desde Ajustes (fallback a defaults hardcodeados). */
export const getDeliveryStatusOptions = () => {
  try {
    const fromCatalog = getActiveDispatchStatusOptions();
    if (fromCatalog.length) {
      return fromCatalog.map((opt) => opt.value);
    }
  } catch {
    /* SSR / storage unavailable */
  }
  return [...DELIVERY_STATUS_OPTIONS];
};

export const getDeliveryStatusCatalogMap = () => {
  const map = new Map();
  try {
    getActiveDispatchStatusOptions().forEach((opt) => {
      const label = String(opt.value || '').trim();
      if (!label) return;
      map.set(label, opt);
      map.set(fold(label), opt);
      if (opt.code) map.set(fold(opt.code), opt);
    });
  } catch {
    /* ignore */
  }
  return map;
};

/** Mapea estados legacy al set actual. */
export const normalizeDeliveryStatus = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Programada';
  const options = getDeliveryStatusOptions();
  if (options.includes(raw) || DELIVERY_STATUS_OPTIONS.includes(raw)) return raw;
  const key = fold(raw);
  const catalogMatch = getDeliveryStatusCatalogMap().get(key);
  if (catalogMatch?.value) return catalogMatch.value;
  if (key === 'pendiente' || key === 'programado') return 'Programada';
  if (key === 'solicitada' || key === 'solicitado') return 'Solicitada';
  if (key === 'reprogramada' || key === 'reprogramado') return 'Reprogramada';
  if (key === 'en reprogramacion' || key === 'en reprogramación') return 'En Reprogramación';
  if (key === 'cancelado' || key === 'cancelada') return 'Cancelado';
  if (key === 'en ruta') return 'En Ruta a Tiempo';
  if (key === 'entregada' || key === 'entregado' || key === 'cerrada' || key === 'cerrado') {
    return 'Entrega Conforme';
  }
  if (key === 'incidencia' || key === 'incidente') return 'Entrega Con Incidente';
  return raw;
};

export const isAwaitingProductionDeliveryStatus = (value) =>
  fold(normalizeDeliveryStatus(value)) === 'solicitada';

export const isRescheduledDeliveryStatus = (value) =>
  fold(normalizeDeliveryStatus(value)) === 'reprogramada';

export const isCancelledDeliveryStatus = (value) => {
  const key = fold(normalizeDeliveryStatus(value));
  return key === 'cancelado' || key === 'cancelada';
};

export const isInTransitDeliveryStatus = (value) => {
  const key = fold(normalizeDeliveryStatus(value));
  return key.startsWith('en ruta');
};

export const isDeliveredDeliveryStatus = (value) => {
  const key = fold(normalizeDeliveryStatus(value));
  return (
    key.startsWith('entrega conforme') ||
    key.startsWith('entrega con retraso') ||
    key === 'entregada' ||
    key === 'entregado'
  );
};

export const isIncidentDeliveryStatus = (value) => {
  const key = fold(normalizeDeliveryStatus(value));
  return key.includes('incidente') || key.includes('incidencia');
};

export const isDelayedDeliveryStatus = (value) => {
  const key = fold(normalizeDeliveryStatus(value));
  return key.includes('retraso');
};

/**
 * Deriva estado operativo (espejo del backend).
 * Entrega Con Incidente es sticky.
 */
export const deriveOperationalDeliveryStatus = ({
  currentStatus,
  promisedDate,
  deliveredDate,
  inTransit = false,
  completed = false,
} = {}) => {
  const current = normalizeDeliveryStatus(currentStatus);
  if (
    isIncidentDeliveryStatus(current) ||
    isAwaitingProductionDeliveryStatus(current) ||
    isRescheduledDeliveryStatus(current) ||
    isCancelledDeliveryStatus(current)
  ) {
    return current;
  }

  const promised = parseYmd(promisedDate);
  const delivered = parseYmd(deliveredDate);
  const today = todayLocal();

  if (completed || isDeliveredDeliveryStatus(current)) {
    const closed = delivered || today;
    if (promised && closed > promised) return 'Entrega con Retraso';
    return 'Entrega Conforme';
  }

  if (inTransit || isInTransitDeliveryStatus(current)) {
    if (promised && today > promised) return 'En Ruta con Retraso';
    return 'En Ruta a Tiempo';
  }

  return 'Programada';
};

const parseYmd = (value) => {
  const raw = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const todayLocal = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const calendarDaysBetween = (from, to) => {
  if (!from || !to) return null;
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
};

/**
 * Días de retraso respecto a la fecha prometida.
 * - En Ruta con Retraso: hoy − prometida
 * - Entrega con Retraso: fecha real − prometida
 */
export const deliveryDelayDays = (row) => {
  if (row?.delay_days != null && Number.isFinite(Number(row.delay_days))) {
    return Math.max(0, Math.floor(Number(row.delay_days)));
  }
  const status = normalizeDeliveryStatus(row?.delivery_status || row?.status);
  const promised = parseYmd(row?.promised_delivery_date || row?.expected_delivery_date);
  if (!promised) return null;

  if (status === 'En Ruta con Retraso') {
    const days = calendarDaysBetween(promised, todayLocal());
    return days != null && days >= 0 ? days : null;
  }
  if (status === 'Entrega con Retraso') {
    const delivered = parseYmd(row?.delivered_date);
    if (!delivered) return null;
    const days = calendarDaysBetween(promised, delivered);
    return days != null && days >= 0 ? days : null;
  }
  return null;
};

export const formatDeliveryDelayLabel = (days) => {
  if (days == null || !Number.isFinite(Number(days))) return null;
  const n = Math.floor(Number(days));
  if (n <= 0) return null;
  return n === 1 ? '1 día de retraso' : `${n} días de retraso`;
};

export const DELIVERY_STATUS_STYLES = {
  Solicitada: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  Programada: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  Reprogramada: { bg: '#f3e8ff', text: '#7e22ce', border: '#d8b4fe' },
  'En Reprogramación': { bg: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' },
  Cancelado: { bg: '#f4f4f5', text: '#52525b', border: '#d4d4d8' },
  'En Ruta a Tiempo': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  'En Ruta con Retraso': { bg: '#fed7aa', text: '#9a3412', border: '#fb923c' },
  'Entrega Con Incidente': { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  'Entrega Conforme': { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  'Entrega con Retraso': { bg: '#fef3c7', text: '#a16207', border: '#fcd34d' },
  // legacy
  Pendiente: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  'En Ruta': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  Entregada: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Incidencia: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  default: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
};

export const DELIVERY_STATUS_COLORS = {
  Solicitada: '#64748b',
  Programada: '#1d4ed8',
  Reprogramada: '#9333ea',
  'En Reprogramación': '#7c3aed',
  Cancelado: '#71717a',
  'En Ruta a Tiempo': '#c2410c',
  'En Ruta con Retraso': '#9a3412',
  'Entrega Con Incidente': '#b91c1c',
  'Entrega Conforme': '#166534',
  'Entrega con Retraso': '#a16207',
  Pendiente: '#64748b',
  'En Ruta': '#c2410c',
  Entregada: '#166534',
  Incidencia: '#b91c1c',
};

/** Estilos de badge (tabla) según Ajustes o fallback. */
export const getDeliveryStatusStyle = (status) => {
  const normalized = normalizeDeliveryStatus(status);
  const catalog = getDeliveryStatusCatalogMap();
  const row = catalog.get(normalized) || catalog.get(fold(normalized));
  if (row?.color_hex) {
    return badgeStylesFromHex(row.color_hex, DELIVERY_STATUS_STYLES[normalized] || DELIVERY_STATUS_STYLES.default);
  }
  return DELIVERY_STATUS_STYLES[normalized] || DELIVERY_STATUS_STYLES.default;
};

/** Color sólido (calendario / chips) según Ajustes o fallback. */
export const getDeliveryStatusColor = (status) => {
  const normalized = normalizeDeliveryStatus(status);
  const catalog = getDeliveryStatusCatalogMap();
  const row = catalog.get(normalized) || catalog.get(fold(normalized));
  if (row?.color_hex) return row.color_hex;
  return DELIVERY_STATUS_COLORS[normalized] || '#2563eb';
};
